import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    ChevronDown,
    ClipboardList,
    HeartPulse,
    Loader2,
    Save,
    Smartphone,
    Stethoscope,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import Badge from '../ui/Badge';
import {
    PAR_Q_KEYS,
    createEmptyAnamnesis,
    isAnamnesisSectionDirty,
    normalizeAnamnesisFromApi,
    prepareAnamnesisForSave,
} from '../../utils/memberAnamnesis';

function AnamnesisSection({
    icon: Icon,
    title,
    description,
    children,
    defaultOpen = false,
    dirty = false,
    showSave = false,
    saving = false,
    onSave,
    saveLabel,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const panelId = useMemo(
        () => `anamnesis-section-${title.replace(/\s+/g, '-').toLowerCase()}`,
        [title],
    );

    return (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-800">
            <div
                className={`flex w-full flex-wrap items-start justify-between gap-3 bg-gradient-to-r from-gray-50 to-white px-5 py-4 dark:from-gray-900/80 dark:to-gray-800 ${
                    open ? 'border-b border-gray-100 dark:border-white/5' : ''
                }`}
            >
                <button
                    type="button"
                    onClick={() => setOpen((prev) => !prev)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left transition-colors hover:opacity-90"
                >
                    {Icon ? (
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                            <Icon size={18} aria-hidden />
                        </span>
                    ) : null}
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white lg:text-lg">{title}</h3>
                        {description ? (
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/60">{description}</p>
                        ) : null}
                    </div>
                </button>
                <ChevronDown
                    size={20}
                    className={`mt-2 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </div>
            {open ? (
                <div id={panelId} className="space-y-4 px-5 py-5">
                    {children}
                    {showSave && dirty ? (
                        <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-white/5">
                            <button
                                type="button"
                                onClick={onSave}
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                ) : (
                                    <Save size={16} aria-hidden />
                                )}
                                {saveLabel}
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
}

function FieldLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-white/80">
            {children}
        </label>
    );
}

function TextAreaField({ id, label, value, onChange, disabled, rows = 3 }) {
    return (
        <div>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <textarea
                id={id}
                rows={rows}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-gray-900 dark:text-white"
            />
        </div>
    );
}

function TextField({ id, label, value, onChange, disabled, type = 'text' }) {
    return (
        <div>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-gray-900 dark:text-white"
            />
        </div>
    );
}

function SelectField({ id, label, value, onChange, disabled, options }) {
    return (
        <div>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-gray-900 dark:text-white"
            >
                <option value="">{options.placeholder || '-'}</option>
                {options.items.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function YesNoField({ name, label, value, note, onAnswer, onNote, disabled, noteLabel, t }) {
    return (
        <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-white/5 dark:bg-gray-900/50">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
            <div className="mt-3 flex flex-wrap gap-4">
                {['yes', 'no'].map((opt) => (
                    <label key={opt} className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
                        <input
                            type="radio"
                            name={name}
                            value={opt}
                            checked={value === opt}
                            onChange={() => onAnswer(opt)}
                            disabled={disabled}
                            className="text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700 dark:text-white/85">
                            {opt === 'yes'
                                ? t('gym.membersManager.memberDetail.profile.yes')
                                : t('gym.membersManager.memberDetail.profile.no')}
                        </span>
                    </label>
                ))}
            </div>
            {value === 'yes' ? (
                <div className="mt-3">
                    <FieldLabel htmlFor={`${name}-note`}>{noteLabel}</FieldLabel>
                    <input
                        id={`${name}-note`}
                        type="text"
                        value={note}
                        onChange={(e) => onNote(e.target.value)}
                        disabled={disabled}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-gray-900 dark:text-white"
                    />
                </div>
            ) : null}
        </div>
    );
}

function statusBadgeVariant(status) {
    if (status === 'complete') return 'success';
    if (status === 'partial') return 'warning';
    return 'secondary';
}

export default function MemberAnamnesisTab({ member, canModify, onGoToConsents }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [savingSection, setSavingSection] = useState(null);
    const [form, setForm] = useState(createEmptyAnamnesis);
    const [savedForm, setSavedForm] = useState(createEmptyAnamnesis);
    const [meta, setMeta] = useState(null);
    const [mobileProfile, setMobileProfile] = useState(null);
    const [healthSummary, setHealthSummary] = useState(null);
    const [healthConsent, setHealthConsent] = useState({ granted: false });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const api = getAuthenticatedApi();
            const res = await api.get(`gym_member_anamnesis.php?user_id=${member.id}`);
            const normalized = normalizeAnamnesisFromApi(res.data?.anamnesis);
            setForm(normalized);
            setSavedForm(normalized);
            setMeta(res.data?.meta ?? null);
            setMobileProfile(res.data?.mobile_profile ?? null);
            setHealthSummary(res.data?.health_summary ?? null);
            setHealthConsent(res.data?.health_consent ?? { granted: false });
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.anamnesis.loadError'));
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, member.id, member.health_data_consent, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const healthConsentGranted = healthConsent?.granted === true;
    const canEdit = canModify && healthConsentGranted;

    const updateSection = (section, field, value) => {
        setForm((prev) => ({
            ...prev,
            [section]: { ...prev[section], [field]: value },
        }));
    };

    const updateParQ = (key, patch) => {
        setForm((prev) => ({
            ...prev,
            par_q: {
                ...prev.par_q,
                [key]: { ...prev.par_q[key], ...patch },
            },
        }));
    };

    const handleSaveSection = async (sectionId) => {
        if (!canEdit) return;
        setSavingSection(sectionId);
        try {
            const api = getAuthenticatedApi();
            const res = await api.put(`gym_member_anamnesis.php?user_id=${member.id}`, {
                anamnesis: prepareAnamnesisForSave(form),
            });
            const normalized = normalizeAnamnesisFromApi(res.data?.anamnesis);
            setForm(normalized);
            setSavedForm(normalized);
            setMeta(res.data?.meta ?? null);
            toast.success(t('gym.membersManager.anamnesis.saveSuccess'));
        } catch (err) {
            toast.error(err.response?.data?.error || t('gym.membersManager.anamnesis.saveError'));
        } finally {
            setSavingSection(null);
        }
    };

    const sectionSaveProps = (sectionId) => ({
        dirty: isAnamnesisSectionDirty(form, savedForm, sectionId),
        showSave: canEdit,
        saving: savingSection === sectionId,
        onSave: () => handleSaveSection(sectionId),
        saveLabel: t('gym.membersManager.anamnesis.saveSection'),
    });

    const parPositive = meta?.par_q_positive_count ?? 0;
    const completionStatus = meta?.completion_status ?? 'empty';

    const occupationOptions = useMemo(
        () => ({
            placeholder: t('gym.membersManager.anamnesis.selectPlaceholder'),
            items: [
                { value: 'sedentary', label: t('gym.membersManager.anamnesis.occupation.sedentary') },
                { value: 'mixed', label: t('gym.membersManager.anamnesis.occupation.mixed') },
                { value: 'physical', label: t('gym.membersManager.anamnesis.occupation.physical') },
            ],
        }),
        [t],
    );

    const experienceOptions = useMemo(
        () => ({
            placeholder: t('gym.membersManager.anamnesis.selectPlaceholder'),
            items: [
                { value: 'beginner', label: t('gym.membersManager.anamnesis.experience.beginner') },
                { value: 'intermediate', label: t('gym.membersManager.anamnesis.experience.intermediate') },
                { value: 'advanced', label: t('gym.membersManager.anamnesis.experience.advanced') },
            ],
        }),
        [t],
    );

    const smokingOptions = useMemo(
        () => ({
            placeholder: t('gym.membersManager.anamnesis.selectPlaceholder'),
            items: [
                { value: 'no', label: t('gym.membersManager.anamnesis.smoking.no') },
                { value: 'former', label: t('gym.membersManager.anamnesis.smoking.former') },
                { value: 'yes', label: t('gym.membersManager.anamnesis.smoking.yes') },
            ],
        }),
        [t],
    );

    const alcoholOptions = useMemo(
        () => ({
            placeholder: t('gym.membersManager.anamnesis.selectPlaceholder'),
            items: [
                { value: 'no', label: t('gym.membersManager.anamnesis.alcohol.no') },
                { value: 'occasional', label: t('gym.membersManager.anamnesis.alcohol.occasional') },
                { value: 'regular', label: t('gym.membersManager.anamnesis.alcohol.regular') },
            ],
        }),
        [t],
    );

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('gym.membersManager.anamnesis.title')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                        {t('gym.membersManager.anamnesis.subtitle')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={statusBadgeVariant(completionStatus)}>
                        {t(`gym.membersManager.anamnesis.status.${completionStatus}`)}
                    </Badge>
                    {meta?.updated_at ? (
                        <span className="text-xs text-gray-500 dark:text-white/55">
                            {t('gym.membersManager.anamnesis.lastUpdate')}:{' '}
                            {new Date(meta.updated_at).toLocaleString('it-IT')}
                        </span>
                    ) : null}
                </div>
            </div>

            <p className="rounded-xl border border-amber-200/80 bg-amber-50/70 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-950/20 dark:text-amber-100">
                {t('gym.membersManager.anamnesis.gdprNote')}
            </p>

            {!healthConsentGranted ? (
                <div className="flex flex-col gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-4 dark:border-orange-500/30 dark:bg-orange-950/30 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600 dark:text-orange-400" aria-hidden />
                        <p className="text-sm text-orange-900 dark:text-orange-100">
                            {t('gym.membersManager.anamnesis.consentRequired')}
                        </p>
                    </div>
                    {canModify && onGoToConsents ? (
                        <button
                            type="button"
                            onClick={onGoToConsents}
                            className="shrink-0 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
                        >
                            {t('gym.membersManager.anamnesis.goToConsents')}
                        </button>
                    ) : null}
                </div>
            ) : null}

            {(mobileProfile || healthSummary) ? (
                <div className="grid gap-4 md:grid-cols-2">
                    {mobileProfile ? (
                        <div className="rounded-xl border border-sky-200/80 bg-sky-50/60 p-4 dark:border-sky-500/20 dark:bg-sky-950/20">
                            <div className="mb-2 flex items-center gap-2 text-sky-800 dark:text-sky-200">
                                <Smartphone size={18} aria-hidden />
                                <h4 className="text-sm font-semibold">{t('gym.membersManager.anamnesis.mobileBannerTitle')}</h4>
                            </div>
                            <dl className="space-y-1 text-sm text-gray-700 dark:text-white/80">
                                {mobileProfile.height != null ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.mobile.height')}: </dt><dd className="inline">{mobileProfile.height} cm</dd></div>
                                ) : null}
                                {mobileProfile.weight != null ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.mobile.weight')}: </dt><dd className="inline">{mobileProfile.weight} kg</dd></div>
                                ) : null}
                                {mobileProfile.fitness_goals ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.mobile.goals')}: </dt><dd className="inline">{mobileProfile.fitness_goals}</dd></div>
                                ) : null}
                                {mobileProfile.injuries ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.mobile.injuries')}: </dt><dd className="inline">{mobileProfile.injuries}</dd></div>
                                ) : null}
                            </dl>
                            <p className="mt-2 text-xs text-gray-500 dark:text-white/50">
                                {t('gym.membersManager.anamnesis.readOnlyHint')}
                            </p>
                        </div>
                    ) : null}
                    {healthSummary ? (
                        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/60 p-4 dark:border-emerald-500/20 dark:bg-emerald-950/20">
                            <div className="mb-2 flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                                <Stethoscope size={18} aria-hidden />
                                <h4 className="text-sm font-semibold">{t('gym.membersManager.anamnesis.healthBannerTitle')}</h4>
                            </div>
                            <dl className="space-y-1 text-sm text-gray-700 dark:text-white/80">
                                {healthSummary.medical_certificate ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.health.cert')}: </dt><dd className="inline">{healthSummary.medical_certificate}</dd></div>
                                ) : null}
                                {healthSummary.medical_cert_expiry ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.health.certExpiry')}: </dt><dd className="inline">{healthSummary.medical_cert_expiry}</dd></div>
                                ) : null}
                                {healthSummary.insurance_number ? (
                                    <div><dt className="inline font-medium">{t('gym.membersManager.anamnesis.health.insurance')}: </dt><dd className="inline">{healthSummary.insurance_number}</dd></div>
                                ) : null}
                            </dl>
                            <p className="mt-2 text-xs text-gray-500 dark:text-white/50">
                                {t('gym.membersManager.anamnesis.editInProfileHint')}
                            </p>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {parPositive > 0 ? (
                <div className="flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 dark:border-orange-500/30 dark:bg-orange-950/30">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600 dark:text-orange-400" aria-hidden />
                    <p className="text-sm text-orange-900 dark:text-orange-100">
                        {t('gym.membersManager.anamnesis.parQWarning', { count: parPositive })}
                    </p>
                </div>
            ) : null}

            <AnamnesisSection
                icon={HeartPulse}
                title={t('gym.membersManager.anamnesis.sections.parQ.title')}
                description={t('gym.membersManager.anamnesis.sections.parQ.description')}
                {...sectionSaveProps('par_q')}
            >
                <div className="space-y-4">
                    {PAR_Q_KEYS.map((key) => (
                        <YesNoField
                            key={key}
                            name={`par-q-${key}`}
                            label={t(`gym.membersManager.anamnesis.parQ.${key}`)}
                            value={form.par_q[key]?.answer ?? ''}
                            note={form.par_q[key]?.note ?? ''}
                            onAnswer={(ans) => updateParQ(key, { answer: ans })}
                            onNote={(note) => updateParQ(key, { note })}
                            disabled={!canEdit}
                            noteLabel={t('gym.membersManager.anamnesis.parQ.noteLabel')}
                            t={t}
                        />
                    ))}
                </div>
            </AnamnesisSection>

            <AnamnesisSection
                icon={Stethoscope}
                title={t('gym.membersManager.anamnesis.sections.pathological.title')}
                description={t('gym.membersManager.anamnesis.sections.pathological.description')}
                {...sectionSaveProps('pathological')}
            >
                <div className="grid gap-4 md:grid-cols-2">
                    <TextAreaField
                        id="path-conditions"
                        label={t('gym.membersManager.anamnesis.fields.conditions')}
                        value={form.pathological.conditions}
                        onChange={(v) => updateSection('pathological', 'conditions', v)}
                        disabled={!canEdit}
                    />
                    <TextAreaField
                        id="path-medications"
                        label={t('gym.membersManager.anamnesis.fields.medications')}
                        value={form.pathological.medications}
                        onChange={(v) => updateSection('pathological', 'medications', v)}
                        disabled={!canEdit}
                    />
                    <TextAreaField
                        id="path-surgeries"
                        label={t('gym.membersManager.anamnesis.fields.surgeries')}
                        value={form.pathological.surgeries}
                        onChange={(v) => updateSection('pathological', 'surgeries', v)}
                        disabled={!canEdit}
                    />
                    <TextAreaField
                        id="path-allergies"
                        label={t('gym.membersManager.anamnesis.fields.allergies')}
                        value={form.pathological.allergies}
                        onChange={(v) => updateSection('pathological', 'allergies', v)}
                        disabled={!canEdit}
                    />
                </div>
            </AnamnesisSection>

            <AnamnesisSection
                icon={ClipboardList}
                title={t('gym.membersManager.anamnesis.sections.musculoskeletal.title')}
                description={t('gym.membersManager.anamnesis.sections.musculoskeletal.description')}
                {...sectionSaveProps('musculoskeletal')}
            >
                <div className="grid gap-4 md:grid-cols-2">
                    <TextAreaField
                        id="msk-injuries-past"
                        label={t('gym.membersManager.anamnesis.fields.injuriesPast')}
                        value={form.musculoskeletal.injuries_past}
                        onChange={(v) => updateSection('musculoskeletal', 'injuries_past', v)}
                        disabled={!canEdit}
                    />
                    <TextAreaField
                        id="msk-injuries-current"
                        label={t('gym.membersManager.anamnesis.fields.injuriesCurrent')}
                        value={form.musculoskeletal.injuries_current}
                        onChange={(v) => updateSection('musculoskeletal', 'injuries_current', v)}
                        disabled={!canEdit}
                    />
                    <TextAreaField
                        id="msk-limitations"
                        label={t('gym.membersManager.anamnesis.fields.limitations')}
                        value={form.musculoskeletal.limitations}
                        onChange={(v) => updateSection('musculoskeletal', 'limitations', v)}
                        disabled={!canEdit}
                    />
                    <SelectField
                        id="msk-physio"
                        label={t('gym.membersManager.anamnesis.fields.physiotherapy')}
                        value={form.musculoskeletal.physiotherapy}
                        onChange={(v) => updateSection('musculoskeletal', 'physiotherapy', v)}
                        disabled={!canEdit}
                        options={{
                            placeholder: t('gym.membersManager.anamnesis.selectPlaceholder'),
                            items: [
                                { value: 'yes', label: t('gym.membersManager.memberDetail.profile.yes') },
                                { value: 'no', label: t('gym.membersManager.memberDetail.profile.no') },
                            ],
                        }}
                    />
                </div>
            </AnamnesisSection>

            <AnamnesisSection
                title={t('gym.membersManager.anamnesis.sections.lifestyle.title')}
                description={t('gym.membersManager.anamnesis.sections.lifestyle.description')}
                {...sectionSaveProps('lifestyle')}
            >
                <div className="grid gap-4 md:grid-cols-2">
                    <SelectField
                        id="life-occupation"
                        label={t('gym.membersManager.anamnesis.fields.occupation')}
                        value={form.lifestyle.occupation}
                        onChange={(v) => updateSection('lifestyle', 'occupation', v)}
                        disabled={!canEdit}
                        options={occupationOptions}
                    />
                    <TextField
                        id="life-sleep"
                        label={t('gym.membersManager.anamnesis.fields.sleepHours')}
                        type="number"
                        value={form.lifestyle.sleep_hours}
                        onChange={(v) => updateSection('lifestyle', 'sleep_hours', v)}
                        disabled={!canEdit}
                    />
                    <SelectField
                        id="life-smoking"
                        label={t('gym.membersManager.anamnesis.fields.smoking')}
                        value={form.lifestyle.smoking}
                        onChange={(v) => updateSection('lifestyle', 'smoking', v)}
                        disabled={!canEdit}
                        options={smokingOptions}
                    />
                    <SelectField
                        id="life-alcohol"
                        label={t('gym.membersManager.anamnesis.fields.alcohol')}
                        value={form.lifestyle.alcohol}
                        onChange={(v) => updateSection('lifestyle', 'alcohol', v)}
                        disabled={!canEdit}
                        options={alcoholOptions}
                    />
                    <div className="md:col-span-2">
                        <TextAreaField
                            id="life-notes"
                            label={t('gym.membersManager.anamnesis.fields.lifestyleNotes')}
                            value={form.lifestyle.notes}
                            onChange={(v) => updateSection('lifestyle', 'notes', v)}
                            disabled={!canEdit}
                        />
                    </div>
                </div>
            </AnamnesisSection>

            <AnamnesisSection
                title={t('gym.membersManager.anamnesis.sections.training.title')}
                description={t('gym.membersManager.anamnesis.sections.training.description')}
                {...sectionSaveProps('training')}
            >
                <div className="grid gap-4 md:grid-cols-2">
                    <TextAreaField
                        id="train-background"
                        label={t('gym.membersManager.anamnesis.fields.sportsBackground')}
                        value={form.training_history.sports_background}
                        onChange={(v) => updateSection('training_history', 'sports_background', v)}
                        disabled={!canEdit}
                    />
                    <TextField
                        id="train-frequency"
                        label={t('gym.membersManager.anamnesis.fields.currentFrequency')}
                        value={form.training_history.current_frequency}
                        onChange={(v) => updateSection('training_history', 'current_frequency', v)}
                        disabled={!canEdit}
                    />
                    <SelectField
                        id="train-experience"
                        label={t('gym.membersManager.anamnesis.fields.experienceLevel')}
                        value={form.training_history.experience_level}
                        onChange={(v) => updateSection('training_history', 'experience_level', v)}
                        disabled={!canEdit}
                        options={experienceOptions}
                    />
                    <TextField
                        id="goals-primary"
                        label={t('gym.membersManager.anamnesis.fields.primaryGoal')}
                        value={form.goals.primary}
                        onChange={(v) => updateSection('goals', 'primary', v)}
                        disabled={!canEdit}
                    />
                    <TextField
                        id="goals-secondary"
                        label={t('gym.membersManager.anamnesis.fields.secondaryGoal')}
                        value={form.goals.secondary}
                        onChange={(v) => updateSection('goals', 'secondary', v)}
                        disabled={!canEdit}
                    />
                    <TextField
                        id="goals-date"
                        label={t('gym.membersManager.anamnesis.fields.targetDate')}
                        type="date"
                        value={form.goals.target_date}
                        onChange={(v) => updateSection('goals', 'target_date', v)}
                        disabled={!canEdit}
                    />
                    <div className="md:col-span-2">
                        <TextAreaField
                            id="goals-note"
                            label={t('gym.membersManager.anamnesis.fields.targetNote')}
                            value={form.goals.target_note}
                            onChange={(v) => updateSection('goals', 'target_note', v)}
                            disabled={!canEdit}
                            rows={2}
                        />
                    </div>
                </div>
            </AnamnesisSection>

            <AnamnesisSection
                title={t('gym.membersManager.anamnesis.sections.professional.title')}
                description={t('gym.membersManager.anamnesis.sections.professional.description')}
                {...sectionSaveProps('professional')}
            >
                <TextAreaField
                    id="prof-notes"
                    label={t('gym.membersManager.anamnesis.fields.professionalNotes')}
                    value={form.professional_notes}
                    onChange={(v) => setForm((prev) => ({ ...prev, professional_notes: v }))}
                    disabled={!canEdit}
                    rows={4}
                />
                <TextField
                    id="next-review"
                    label={t('gym.membersManager.anamnesis.fields.nextReviewDate')}
                    type="date"
                    value={form.next_review_date}
                    onChange={(v) => setForm((prev) => ({ ...prev, next_review_date: v }))}
                    disabled={!canEdit}
                />
            </AnamnesisSection>
        </div>
    );
}
